/**
 * PrintNoteView - Renders a clinical note for PDF generation
 * 
 * This component is designed to be accessed via a print token URL.
 * Playwright navigates here, waits for window.renderFinished, then captures the PDF.
 * 
 * Features:
 * - 1:1 rendering matching the UI
 * - @media print CSS for paper optimization
 * - UUID → human-readable label resolution
 * - All template fields shown (even if empty)
 * - Sets window.renderFinished when fully loaded
 */

import React, { useState, useEffect } from 'react';
import { formatMrn } from '../../utils/formatMrn';
import { useParams, useSearchParams } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

// Field type mappings for display
const FIELD_TYPE_LABELS = {
  'checkbox': 'checkbox',
  'boolean': 'checkbox',
  'toggle': 'checkbox',
  'switch': 'checkbox',
  'select': 'select',
  'dropdown': 'select',
  'radio': 'radio',
  'multi-select': 'multi-select',
  'multiselect': 'multi-select',
  'checkbox-group': 'multi-select',
};

/**
 * Resolve a value to its human-readable label using field options
 */
const resolveLabel = (value, options = []) => {
  if (!value || !options || options.length === 0) return value;
  
  const strValue = String(value).trim();
  
  for (const opt of options) {
    if (typeof opt === 'object') {
      const optValue = String(opt.value || '');
      const optLabel = opt.label || opt.text || optValue;
      const optId = String(opt.id || '');
      
      if (strValue === optValue || strValue === optId || strValue === optLabel) {
        return optLabel;
      }
    } else {
      if (strValue === String(opt)) {
        return String(opt);
      }
    }
  }
  
  return strValue;
};

/**
 * Resolve multiple values (multi-select) to labels
 */
const resolveMultiLabels = (values, options = []) => {
  if (!values) return '';
  
  let valueList = values;
  if (typeof values === 'string') {
    valueList = values.split(',').map(v => v.trim());
  }
  if (!Array.isArray(valueList)) {
    valueList = [valueList];
  }
  
  return valueList.map(v => resolveLabel(v, options)).filter(Boolean).join(', ');
};

/**
 * Format boolean/checkbox value for display
 */
const formatCheckbox = (value) => {
  if (value === true || value === 'true' || value === 'yes' || value === '1' || value === 'on') {
    return '[X] Yes';
  }
  return '[ ] No';
};

/**
 * Check if a value looks like a UUID
 */
const isUUID = (value) => {
  if (typeof value !== 'string') return false;
  // Standard UUID
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) return true;
  // UUID without dashes
  if (/^[0-9a-f]{32}$/i.test(value)) return true;
  // Partial UUID (8+ hex chars, all uppercase)
  if (/^[0-9A-F]{8,}$/.test(value)) return true;
  return false;
};

/**
 * Single field renderer
 */
const PrintField = ({ field, value, options = [] }) => {
  const fieldType = FIELD_TYPE_LABELS[field.type] || field.type || 'text';
  
  let displayValue = '';
  
  // Handle different field types
  if (fieldType === 'checkbox') {
    displayValue = formatCheckbox(value);
  } else if (fieldType === 'select' || fieldType === 'radio' || fieldType === 'dropdown') {
    displayValue = resolveLabel(value, options) || 'Not documented';
  } else if (fieldType === 'multi-select') {
    displayValue = resolveMultiLabels(value, options) || 'Not documented';
  } else if (value === null || value === undefined || value === '') {
    displayValue = 'Not documented';
  } else if (isUUID(value)) {
    displayValue = 'Not documented'; // Don't show raw UUIDs
  } else if (typeof value === 'object') {
    // Handle object values (extract meaningful content)
    if (value.label) displayValue = value.label;
    else if (value.text) displayValue = value.text;
    else if (value.name) displayValue = value.name;
    else displayValue = 'Not documented';
  } else {
    displayValue = String(value);
  }
  
  const isEmpty = displayValue === 'Not documented' || displayValue === '[ ] No';
  
  return (
    <div className="print-field">
      <div className="print-field-label">{field.label || field.name || field.id}</div>
      <div className={`print-field-value ${isEmpty ? 'empty' : ''} ${fieldType === 'checkbox' ? 'checkbox' : ''}`}>
        {displayValue}
      </div>
    </div>
  );
};

/**
 * Main Print Note View Component
 */
const PrintNoteView = () => {
  const { noteId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchPrintData = async () => {
      if (!token) {
        setError('Missing print token');
        setLoading(false);
        return;
      }
      
      try {
        const response = await fetch(`${API}/api/print/note/${noteId}?token=${encodeURIComponent(token)}`);
        
        if (!response.ok) {
          const errData = await response.json();
          throw new Error(errData.detail || 'Failed to load note');
        }
        
        const printData = await response.json();
        setData(printData);
        setLoading(false);
        
        // Signal to Playwright that rendering is complete
        setTimeout(() => {
          window.renderFinished = true;
          void 0 && console.log('[PrintNoteView] Render finished, window.renderFinished = true');
        }, 100);
        
      } catch (err) {
        console.error('[PrintNoteView] Error:', err);
        setError(err.message);
        setLoading(false);
        window.renderFinished = true; // Still signal completion on error
      }
    };
    
    fetchPrintData();
  }, [noteId, token]);
  
  if (loading) {
    return (
      <div className="print-loading">
        <div className="print-loading-spinner"></div>
        <p>Loading clinical note...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="print-error">
        <h1>Error Loading Note</h1>
        <p>{error}</p>
      </div>
    );
  }
  
  if (!data) {
    return (
      <div className="print-error">
        <h1>No Data</h1>
        <p>Unable to load note data.</p>
      </div>
    );
  }
  
  const { note, client, provider, template, branding, signatures, diagnoses, treatment_goals } = data;
  
  // Extract form data
  const formData = note.form_data || note.content || {};
  
  // Build field metadata map from template
  const fieldMetadata = {};
  let templateFields = [];
  
  if (template) {
    if (template.fields && template.fields.length > 0) {
      templateFields = template.fields;
    } else if (template.form_fields && template.form_fields.length > 0) {
      // Common pattern: form_fields array
      templateFields = template.form_fields;
    } else if (template.steps) {
      // Intake-style template with steps
      template.steps.forEach(step => {
        if (step.fields) {
          templateFields = [...templateFields, ...step.fields];
        }
      });
    } else if (template.sections) {
      // Section-based template
      template.sections.forEach(section => {
        if (section.fields) {
          templateFields = [...templateFields, ...section.fields];
        }
      });
    }
    
    templateFields.forEach(field => {
      const fieldId = field.id || field.field_id || field.name;
      if (fieldId) {
        fieldMetadata[fieldId] = {
          ...field,
          options: field.options || []
        };
      }
    });
  }
  
  // Build diagnosis lookup map
  const diagnosisMap = {};
  diagnoses.forEach(d => {
    diagnosisMap[d.id] = `${d.diagnosis_code} - ${d.description}`;
  });
  
  // Build treatment goal lookup map
  const goalMap = {};
  treatment_goals.forEach(g => {
    goalMap[g.id] = g.goal_text;
  });
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };
  
  // Render all fields from template (even empty ones)
  const renderFields = () => {
    // Helper to convert label to possible form_data keys
    const labelToKeys = (label) => {
      if (!label) return [];
      const snakeCase = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
      const camelCase = label.toLowerCase().replace(/[^a-z0-9]+(.)/g, (_, c) => c.toUpperCase());
      return [snakeCase, camelCase, label];
    };
    
    if (templateFields.length === 0) {
      // No template - render form_data as-is
      return Object.entries(formData).map(([key, value]) => {
        if (key.startsWith('_') || key === 'id' || key === 'note_id' || key === 'template_id') {
          return null;
        }
        return (
          <PrintField
            key={key}
            field={{ id: key, label: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) }}
            value={value}
          />
        );
      }).filter(Boolean);
    }
    
    // Render all template fields in order
    return templateFields.map((field, index) => {
      const fieldId = field.id || field.field_id || field.name;
      if (!fieldId) return null;
      
      // Skip section headers for now (they're display-only)
      if (field.type === 'section_header') {
        return (
          <div key={fieldId} className="print-section-header">
            <h4>{field.label}</h4>
          </div>
        );
      }
      
      // Check visible - default to true if not specified
      if (field.visible === false) return null;
      
      // Try to find value - check by field ID first, then by label variations
      let value = formData[fieldId];
      if (value === undefined && field.label) {
        const possibleKeys = labelToKeys(field.label);
        for (const key of possibleKeys) {
          if (formData[key] !== undefined) {
            value = formData[key];
            break;
          }
        }
      }
      
      // Check for diagnosis/goal references and resolve them
      if (fieldId.toLowerCase().includes('diagnosis') && isUUID(value)) {
        value = diagnosisMap[value] || value;
      }
      if (fieldId.toLowerCase().includes('goal') && isUUID(value)) {
        value = goalMap[value] || value;
      }
      
      return (
        <PrintField
          key={fieldId || index}
          field={field}
          value={value}
          options={field.options || []}
        />
      );
    }).filter(Boolean);
  };
  
  return (
    <div className="print-container">
      {/* Header */}
      <div className="print-header">
        {branding?.logo_url && (
          <img src={branding.logo_url} alt="Logo" className="print-logo" />
        )}
        <div className="print-org-info">
          <h1 className="print-org-name">{branding?.practice_name || 'Clinical Documentation'}</h1>
          {branding?.address && <p>{branding.address}</p>}
          {branding?.phone && <p>Phone: {branding.phone}</p>}
        </div>
      </div>
      
      {/* Document Title */}
      <div className="print-title">
        <h2>{template?.name || note.template_name || 'Clinical Note'}</h2>
        <div className="print-meta">
          <span>Date of Service: {formatDate(note.date_of_service || note.created_at)}</span>
          <span className={`print-status ${note.status}`}>
            {note.status === 'signed' ? 'SIGNED' : note.status === 'locked' ? 'LOCKED' : note.status?.toUpperCase()}
          </span>
        </div>
      </div>
      
      {/* Client & Provider Info */}
      <div className="print-info-grid">
        <div className="print-info-box">
          <h3>Client Information</h3>
          <p><strong>Name:</strong> {client?.full_name || `${client?.first_name || ''} ${client?.last_name || ''}`.trim() || 'N/A'}</p>
          <p><strong>DOB:</strong> {formatDate(client?.date_of_birth) || 'N/A'}</p>
          <p><strong>MRN:</strong> {formatMrn(client?.mrn) || 'N/A'}</p>
        </div>
        <div className="print-info-box">
          <h3>Provider Information</h3>
          <p><strong>Name:</strong> {provider?.full_name || 'N/A'}</p>
          <p><strong>Credentials:</strong> {provider?.credentials || 'N/A'}</p>
          <p><strong>NPI:</strong> {provider?.npi || 'N/A'}</p>
        </div>
      </div>
      
      {/* Clinical Content */}
      <div className="print-content">
        <h3>Clinical Documentation</h3>
        <div className="print-fields">
          {renderFields()}
        </div>
      </div>
      
      {/* Signatures */}
      {Object.keys(signatures).length > 0 && (
        <div className="print-signatures">
          <h3>Signatures</h3>
          <div className="print-signature-grid">
            {signatures.provider && (
              <div className="print-signature-box">
                <p><strong>Provider Signature</strong></p>
                {signatures.provider.signature_url && (
                  <img src={signatures.provider.signature_url} alt="Provider Signature" className="print-signature-img" />
                )}
                <p>{signatures.provider.name}, {signatures.provider.credentials}</p>
                <p>Signed: {formatDate(signatures.provider.signed_at)}</p>
              </div>
            )}
            {signatures.supervisor && (
              <div className="print-signature-box">
                <p><strong>Supervisor Signature</strong></p>
                {signatures.supervisor.signature_url && (
                  <img src={signatures.supervisor.signature_url} alt="Supervisor Signature" className="print-signature-img" />
                )}
                <p>{signatures.supervisor.name}, {signatures.supervisor.credentials}</p>
                <p>Signed: {formatDate(signatures.supervisor.signed_at)}</p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Footer */}
      <div className="print-footer">
        <p>Generated: {new Date().toLocaleString()}</p>
        <p>Document ID: {note.doc_number || note.id}</p>
        <p className="print-confidential">CONFIDENTIAL - Protected Health Information</p>
      </div>
      
      {/* Print Styles */}
      <style>{`
        /* Base Print Styles */
        .print-container {
          font-family: 'Times New Roman', Times, serif;
          font-size: 11pt;
          line-height: 1.4;
          color: #000;
          background: #fff;
          padding: 0.5in;
          max-width: 8.5in;
          margin: 0 auto;
        }
        
        .print-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
        }
        
        .print-loading-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #e5e7eb;
          border-top-color: #0d9488;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        .print-error {
          padding: 2rem;
          text-align: center;
          color: #dc2626;
        }
        
        /* Header */
        .print-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid #000;
          margin-bottom: 1rem;
        }
        
        .print-logo {
          max-height: 60px;
          max-width: 150px;
        }
        
        .print-org-name {
          font-size: 16pt;
          font-weight: bold;
          margin: 0;
        }
        
        .print-org-info p {
          margin: 2px 0;
          font-size: 9pt;
        }
        
        /* Title */
        .print-title {
          text-align: center;
          margin-bottom: 1rem;
        }
        
        .print-title h2 {
          font-size: 14pt;
          font-weight: bold;
          margin: 0 0 0.25rem 0;
          text-transform: uppercase;
        }
        
        .print-meta {
          display: flex;
          justify-content: center;
          gap: 2rem;
          font-size: 10pt;
        }
        
        .print-status {
          font-weight: bold;
          padding: 2px 8px;
          border: 1px solid #000;
        }
        
        .print-status.signed, .print-status.locked {
          background: #d1fae5;
        }
        
        .print-status.draft {
          background: #fef3c7;
        }
        
        /* Info Grid */
        .print-info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin-bottom: 1rem;
        }
        
        .print-info-box {
          border: 1px solid #000;
          padding: 0.5rem;
        }
        
        .print-info-box h3 {
          font-size: 11pt;
          font-weight: bold;
          margin: 0 0 0.25rem 0;
          border-bottom: 1px solid #ccc;
          padding-bottom: 0.25rem;
        }
        
        .print-info-box p {
          margin: 2px 0;
          font-size: 10pt;
        }
        
        /* Content */
        .print-content {
          margin-bottom: 1rem;
        }
        
        .print-content h3 {
          font-size: 12pt;
          font-weight: bold;
          margin: 0 0 0.5rem 0;
          border-bottom: 1px solid #000;
          padding-bottom: 0.25rem;
        }
        
        .print-fields {
          display: grid;
          gap: 0.5rem;
        }
        
        /* Field Styles */
        .print-field {
          display: grid;
          grid-template-columns: 200px 1fr;
          gap: 0.5rem;
          padding: 0.25rem 0;
          border-bottom: 1px dotted #ccc;
        }
        
        .print-section-header {
          margin-top: 1rem;
          margin-bottom: 0.5rem;
          padding: 0.25rem 0;
          border-bottom: 2px solid #0d9488;
        }
        
        .print-section-header h4 {
          font-size: 11pt;
          font-weight: bold;
          color: #0d9488;
          margin: 0;
          text-transform: uppercase;
        }
        
        .print-field-label {
          font-weight: bold;
          font-size: 10pt;
        }
        
        .print-field-value {
          font-size: 10pt;
        }
        
        .print-field-value.empty {
          color: #6b7280;
          font-style: italic;
        }
        
        .print-field-value.checkbox {
          font-family: 'Courier New', monospace;
          font-weight: 500;
        }
        
        /* Signatures */
        .print-signatures {
          margin-top: 2rem;
          page-break-inside: avoid;
        }
        
        .print-signatures h3 {
          font-size: 12pt;
          font-weight: bold;
          margin: 0 0 0.5rem 0;
          border-bottom: 1px solid #000;
          padding-bottom: 0.25rem;
        }
        
        .print-signature-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }
        
        .print-signature-box {
          border: 1px solid #000;
          padding: 0.5rem;
        }
        
        .print-signature-img {
          max-height: 60px;
          max-width: 200px;
        }
        
        /* Footer */
        .print-footer {
          margin-top: 2rem;
          padding-top: 0.5rem;
          border-top: 1px solid #000;
          font-size: 8pt;
          text-align: center;
        }
        
        .print-confidential {
          font-weight: bold;
          margin-top: 0.5rem;
        }
        
        /* Print Media Styles */
        @media print {
          body {
            margin: 0;
            padding: 0;
          }
          
          .print-container {
            padding: 0;
            max-width: none;
          }
          
          /* Hide any UI elements that shouldn't print */
          nav, .sidebar, .toolbar, button, .no-print {
            display: none !important;
          }
          
          /* Page breaks */
          .print-header {
            page-break-after: avoid;
          }
          
          .print-signatures {
            page-break-inside: avoid;
          }
          
          .print-field {
            page-break-inside: avoid;
          }
        }
        
        /* Screen styles for preview */
        @media screen {
          body {
            background: #f3f4f6;
          }
          
          .print-container {
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            margin: 1rem auto;
          }
        }
      `}</style>
    </div>
  );
};

export default PrintNoteView;
